import { useParams } from "@tanstack/react-router"
import { Tabs } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { useDefaultAttachmentsApiForControllerRoute } from "@/attachment-api"

import type { ResourceObjectInsurance } from "./types"

import {
  useResourceData,
  useBorrowLogic,
  useEditResourceDetailsLogic,
  useInsuranceLogic,
  useInsuranceFilter,
  useValuationLogic,
} from "./hooks"

import {
  ReturnModal,
  LendModal,
  EditResourceDetailsModal,
  EditObjectFieldsModal,
  EditInsuranceModal,
  CreateInsuranceModal,
  CreateValuationModal,
} from "./modals"

import { StartTab, RentTab, InsuranceTab, ValuationTab } from "./tabs"
import { Page } from "@components/Page"
import { useGetCondition } from "@/orval/gen"

export function ResourceObjectDetailsPage() {
  const resourceObjectId = parseInt(
    useParams({ from: "/app/resources/resource-objects/$id" }).id,
    10
  )
  const { t } = useTranslation("app_server")

  const { resourceObject, resource, insurances, monetaryValues } =
    useResourceData(resourceObjectId)

  const borrowLogic = useBorrowLogic({ resourceObjectId, resourceObject })
  const EditResourceDetailsLogic = useEditResourceDetailsLogic({
    resourceObjectId,
    resourceObject,
  })
  const insuranceLogic = useInsuranceLogic({ resourceObjectId })
  const insuranceFilter = useInsuranceFilter(insurances)
  const valuationLogic = useValuationLogic({
    resourceObjectId,
    monetaryValues,
  })

  const conditionQuery = useGetCondition(
    { conditionId: resourceObject.resourceObjectConditionId ?? 0 },
    { query: { enabled: !!resourceObject.resourceObjectConditionId } }
  )

  const [thumbnailFailed, setThumbnailFailed] = React.useState(false)
  const attachmentApi = useDefaultAttachmentsApiForControllerRoute("resources")

  if (!resourceObject) {
    return (
      <Page>
        <div>{t("NoData")}</div>
      </Page>
    )
  }

  const handleEditObjectFields = () => {
    EditResourceDetailsLogic.setIsEditObjectFieldsModalOpen(true)
  }

  const handleEditInsurance = (insurance: ResourceObjectInsurance) => {
    insuranceLogic.setSelectedInsuranceForEdit(insurance)
    insuranceLogic.setIsEditInsuranceModalOpen(true)
  }

  const handleCreatePrivateInsurance = () => {
    insuranceLogic.setEditInsuranceFormData({
      insuredPersonId: null,
      startDate: new Date(),
      endDate: null,
      isPrivateInsurance: true,
    })
    insuranceLogic.setIsCreateInsuranceModalOpen(true)
  }

  const handleCreateBusinessInsurance = () => {
    insuranceLogic.setEditInsuranceFormData({
      insuredPersonId: null,
      startDate: new Date(),
      endDate: null,
      isPrivateInsurance: false,
    })
    insuranceLogic.setIsCreateInsuranceModalOpen(true)
  }

  return (
    <Page>
      <Tabs defaultValue="start" pt="md">
        <Tabs.List>
          <Tabs.Tab value="start">{t("Start")}</Tabs.Tab>
          <Tabs.Tab value="rent">{t("Rent")}</Tabs.Tab>
          <Tabs.Tab value="insurance">{t("Insurance")}</Tabs.Tab>
          <Tabs.Tab value="valuation">{t("Valuation")}</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="start" pt="md">
          <StartTab
            condition={conditionQuery.data?.name ?? null}
            resourceObject={resourceObject}
            resource={resource}
            resourceObjectId={resourceObjectId}
            bestEstimate={valuationLogic.bestEstimate}
            activeBorrow={borrowLogic.activeBorrow}
            activePrivateInsurances={insuranceFilter.activePrivateInsurances}
            activeBusinessInsurances={insuranceFilter.activeBusinessInsurances}
            thumbnailFailed={thumbnailFailed}
            setThumbnailFailed={setThumbnailFailed}
            onEditResourceDetailsClick={() =>
              EditResourceDetailsLogic.setIsEditResourceDetailsModalOpen(true)
            }
            onLendClick={() => borrowLogic.setIsLendModalOpen(true)}
            onReturnClick={() => borrowLogic.setIsReturnModalOpen(true)}
            onEditObjectFieldsClick={handleEditObjectFields}
            attachmentApi={attachmentApi}
          />
        </Tabs.Panel>

        <Tabs.Panel value="rent" pt="md">
          <RentTab
            activeBorrow={borrowLogic.activeBorrow}
            pastBorrows={borrowLogic.pastBorrows}
            resourceObjectId={resourceObjectId}
            onReturnClick={() => borrowLogic.setIsReturnModalOpen(true)}
            onLendClick={() => borrowLogic.setIsLendModalOpen(true)}
          />
        </Tabs.Panel>

        <Tabs.Panel value="insurance" pt="md">
          <InsuranceTab
            activePrivateInsurances={insuranceFilter.activePrivateInsurances}
            activeBusinessInsurances={insuranceFilter.activeBusinessInsurances}
            historyPrivateInsurances={insuranceFilter.historyPrivateInsurances}
            historyBusinessInsurances={
              insuranceFilter.historyBusinessInsurances
            }
            onEditInsurance={handleEditInsurance}
            onCreatePrivateInsurance={handleCreatePrivateInsurance}
            onCreateBusinessInsurance={handleCreateBusinessInsurance}
          />
        </Tabs.Panel>

        <Tabs.Panel value="valuation" pt="md">
          <ValuationTab
            monetaryValues={monetaryValues}
            onCreateValuation={() =>
              valuationLogic.setIsCreateValuationModalOpen(true)
            }
          />
        </Tabs.Panel>
      </Tabs>

      <ReturnModal
        opened={borrowLogic.isReturnModalOpen}
        onClose={() => borrowLogic.setIsReturnModalOpen(false)}
        returnEndDate={borrowLogic.returnEndDate}
        setReturnEndDate={borrowLogic.setReturnEndDate}
        activeBorrow={borrowLogic.activeBorrow}
        updateBorrowMutation={borrowLogic.updateBorrowMutation}
      />

      <LendModal
        opened={borrowLogic.isLendModalOpen}
        onClose={() => borrowLogic.setIsLendModalOpen(false)}
        lendFormData={borrowLogic.lendFormData}
        setLendFormData={borrowLogic.setLendFormData}
        createBorrowMutation={borrowLogic.createBorrowMutation}
        hasPersonOrLocation={borrowLogic.hasPersonOrLocation}
        personIsRequired={borrowLogic.personIsRequired}
        locationIsRequired={borrowLogic.locationIsRequired}
        formValidationError={borrowLogic.formValidationError}
      />

      <EditResourceDetailsModal
        opened={EditResourceDetailsLogic.isEditResourceDetailsModalOpen}
        onClose={() =>
          EditResourceDetailsLogic.setIsEditResourceDetailsModalOpen(false)
        }
        resourceObjectId={resourceObjectId}
        editFormData={EditResourceDetailsLogic.editFormData}
        setEditFormData={EditResourceDetailsLogic.setEditFormData}
        updateResourceObjectMutation={
          EditResourceDetailsLogic.updateResourceObjectMutation
        }
        createValuationMutation={
          EditResourceDetailsLogic.createValuationMutation
        }
        updateValuationMutation={
          EditResourceDetailsLogic.updateValuationMutation
        }
        monetaryValues={monetaryValues}
      />

      <EditObjectFieldsModal
        opened={EditResourceDetailsLogic.isEditObjectFieldsModalOpen}
        onClose={() =>
          EditResourceDetailsLogic.setIsEditObjectFieldsModalOpen(false)
        }
        editObjectFields={EditResourceDetailsLogic.editObjectFields}
        setEditObjectFields={EditResourceDetailsLogic.setEditObjectFields}
        updateObjectFieldsMutation={
          EditResourceDetailsLogic.updateObjectFieldsMutation
        }
      />

      <EditInsuranceModal
        opened={insuranceLogic.isEditInsuranceModalOpen}
        onClose={() => insuranceLogic.setIsEditInsuranceModalOpen(false)}
        editInsuranceFormData={insuranceLogic.editInsuranceFormData}
        setEditInsuranceFormData={insuranceLogic.setEditInsuranceFormData}
        updateInsuranceMutation={insuranceLogic.updateInsuranceMutation}
        deleteInsuranceMutation={insuranceLogic.deleteInsuranceMutation}
        insuranceId={
          insuranceLogic.selectedInsuranceForEdit?.resourceObjectInsuranceId
        }
        onUpdate={insuranceLogic.handleUpdateInsurance}
        onDelete={insuranceLogic.handleDeleteInsurance}
      />

      <CreateInsuranceModal
        opened={insuranceLogic.isCreateInsuranceModalOpen}
        onClose={() => insuranceLogic.setIsCreateInsuranceModalOpen(false)}
        editInsuranceFormData={insuranceLogic.editInsuranceFormData}
        setEditInsuranceFormData={insuranceLogic.setEditInsuranceFormData}
        createInsuranceMutation={insuranceLogic.createInsuranceMutation}
        onCreate={insuranceLogic.handleCreateInsurance}
      />

      <CreateValuationModal
        opened={valuationLogic.isCreateValuationModalOpen}
        onClose={() => valuationLogic.setIsCreateValuationModalOpen(false)}
        createValuationFormData={valuationLogic.createValuationFormData}
        setCreateValuationFormData={valuationLogic.setCreateValuationFormData}
        createValuationMutation={valuationLogic.createValuationMutation}
        monetaryValues={monetaryValues}
      />
    </Page>
  )
}
